import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../../Elements/Button/Button";
import { initMercadoPago, Wallet } from "@mercadopago/sdk-react";
import "./hacerPedido.css";
import useStore from "../../../../useStore";
import Loader from "../../../Elements/Loader/Loader";
import MapPickerLeaflet from "../../createRestaurant/MapPickerLeaflet/MapPickerLeaflet";
import Modal from "../../../Elements/Modal";
import { useToast } from "../../../../Contexts/Toast/toast.provider";
import { distanceKm, RADIUS_KM_RESTAURANTS } from "../../../../Utilities/geocode";

const HacerPedido = () => {
  const [propinaPct, setPropinaPct] = useState(0);
  const [propina, setPropina] = useState(0);
  const showToast = useToast();
  const PUBLIC_KEY = process.env.REACT_APP_ML_PUBLIC_KEY;

  useEffect(() => {
    if (PUBLIC_KEY) {
      initMercadoPago(PUBLIC_KEY, { locale: "es-MX" });
    }
  }, [PUBLIC_KEY]);

  const { id, orderId } = useParams();
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const {
    loading,
    createPreferencefetch,
    responseCreatePreference,
    loadingCreatePreference,
    errorCreatePreference,
    fetchDataCreateOrder,
    fetchDataGetRestaurantDetail,
    restaurantDetail,
  } = useStore();

  const pedido = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(`pedido-${orderId}`) || "null");
    } catch {
      return null;
    }
  }, [orderId]);

  const userDelivery =
    user?.delivery ?? user?.data?.delivery ?? null;

  const [deliveryOption, setDeliveryOption] = useState("home");
  const [customDelivery, setCustomDelivery] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [showCashModal, setShowCashModal] = useState(false);
  const [cashOrderData, setCashOrderData] = useState(null);
  const [bebidasAgregadas, setBebidasAgregadas] = useState([]);
  const hasHome = !!(userDelivery?.direccion && userDelivery?.lat != null && userDelivery?.lng != null);
  const hasCustom = !!(customDelivery?.direccion && customDelivery?.lat != null && customDelivery?.lng != null);

  // Coordenadas del restaurante (para validar distancia)
  const restLat = restaurantDetail?.lat ?? null;
  const restLng = restaurantDetail?.lng ?? null;
  const restHasCoords = restLat !== null && restLng !== null;

  // Distancia casa del usuario → restaurante
  const distanciaCasa = useMemo(() => {
    if (!restHasCoords || !hasHome) return null;
    return distanceKm(restLat, restLng, Number(userDelivery.lat), Number(userDelivery.lng));
  }, [restHasCoords, restLat, restLng, hasHome, userDelivery]);

  const casaFueraDeRango = distanciaCasa !== null && distanciaCasa > RADIUS_KM_RESTAURANTS;

  // Distancia ubicación custom → restaurante
  const distanciaCustom = useMemo(() => {
    if (!restHasCoords || !hasCustom) return null;
    return distanceKm(restLat, restLng, Number(customDelivery.lat), Number(customDelivery.lng));
  }, [restHasCoords, restLat, restLng, hasCustom, customDelivery]);

  const customFueraDeRango = distanciaCustom !== null && distanciaCustom > RADIUS_KM_RESTAURANTS;

  // Casa fuera de rango → forzar "Otro lugar" automáticamente
  const isReadyDelivery =
    deliveryOption === "home"
      ? (hasHome && !casaFueraDeRango)
      : (hasCustom && !customFueraDeRango);

  const readUser = () => {
    try {
      const raw = localStorage.getItem("logindata");
      if (raw) setUser(JSON.parse(raw));
    } catch { }
  };

  useEffect(() => {
    readUser();
    // Re-leer cuando updateProfile o login actualicen logindata
    window.addEventListener("authChange", readUser);
    return () => window.removeEventListener("authChange", readUser);
  }, []);

  useEffect(() => {
    if (id) fetchDataGetRestaurantDetail(id);
  }, [id, fetchDataGetRestaurantDetail]);

  // Si la casa está fuera del radio, cambiar automáticamente a "Otro lugar"
  useEffect(() => {
    if (casaFueraDeRango && deliveryOption === "home") {
      setDeliveryOption("custom");
    }
  }, [casaFueraDeRango, deliveryOption]);

  const bebidasSugeridas = useMemo(() => {
    if (!restaurantDetail?.productos) return [];
    const todas = restaurantDetail.productos.filter(
      p => p.isAvailable !== false && p.categoria?.toLowerCase() === "bebida"
    );
    return [...todas].sort(() => Math.random() - 0.5).slice(0, 3);
  }, [restaurantDetail]);

  const getLoginField = (key) => user?.[key] ?? user?.data?.[key] ?? null;
  const loginName = getLoginField("name");
  const loginEmail = getLoginField("email");
  const loginPhone = getLoginField("phone");

  if (!pedido) return <p>No se encontró el pedido</p>;

  const extraBebidas = bebidasAgregadas.reduce((sum, b) => sum + Number(b.precio), 0);
  const subtotal = Number(pedido.subtotal || 0) + extraBebidas;
  const envio = Number(pedido.envio || 0);
  const baseTotal = Number(pedido.total || 0) + extraBebidas;

  const handleChangePropina = (value) => {
    setPropinaPct(value);
    const tipAmount = Math.round(((subtotal * value) / 100) * 100) / 100;
    setPropina(tipAmount);
  };

  const cancelarPropina = () => {
    setPropinaPct(0);
    setPropina(0);
  };

  const toggleBebida = (bebida) => {
    setBebidasAgregadas(prev => {
      const existe = prev.some(b => b.name === bebida.name);
      return existe ? prev.filter(b => b.name !== bebida.name) : [...prev, bebida];
    });
  };

  const createPreferenceAction = async (method = "mercadoPago") => {
    // 1) Determinar la ubicación de entrega a usar
    const deliveryToUse =
      deliveryOption === "home"
        ? (hasHome ? { direccion: userDelivery.direccion, lat: userDelivery.lat, lng: userDelivery.lng } : null)
        : (hasCustom ? customDelivery : null);

    if (!deliveryToUse) {
      alert("Selecciona una ubicación de entrega válida (Casa o desde el mapa).");
      if (deliveryOption === "custom") setShowMap(true);
      return;
    }

    // 2) Metadatos base
    const metaDataValues = {
      orderId: pedido.orderId,
      restaurante: id,
      cliente: loginName || pedido.cliente || "Anonimo del front",
      telefono: loginPhone || pedido.telefono || "",
    };

    // 3) Cálculo de totales
    const totalFinal = baseTotal + propina;

    // === NUEVO: arma items para Mercado Pago (sin duplicados y sin precios 0) ===
    const itemsMP = [
      // Productos > 0
      ...pedido.items
        .filter(it => Number(it.precio) > 0 && Number(it.qty) > 0)
        .map(it => ({
          id: String(it.id || it.name),
          title: it.name,
          description: `Producto del pedido ${pedido.orderId}`,
          quantity: Math.round(Number(it.qty)),
          unit_price: Number(Number(it.precio).toFixed(2)),
          currency_id: "MXN",
        })),

      // Bebidas agregadas como sugerencia
      ...bebidasAgregadas.map(b => ({
        id: `bebida-${b.name}`,
        title: b.name,
        description: b.descripcion || "Bebida",
        quantity: 1,
        unit_price: Number(Number(b.precio).toFixed(2)),
        currency_id: "MXN",
      })),

      // Envío solo si > 0
      ...(Number(envio) > 0 ? [{
        id: "envio",
        title: "Costo de envío",
        quantity: 1,
        unit_price: Number(Number(envio).toFixed(2)),
        currency_id: "MXN",
      }] : []),

      // Propina solo si > 0
      ...(Number(propina) > 0 ? [{
        id: "propina",
        title: "Propina al repartidor",
        quantity: 1,
        unit_price: Number(Number(propina).toFixed(2)),
        currency_id: "MXN",
      }] : []),
    ];

    // Evita preferencias sin monto > 0
    if (itemsMP.length === 0) {
      alert("El total a pagar debe ser mayor a $0 MXN.");
      return;
    }

    // === NUEVO: asegura payer.email (usa el del login o el del token) ===
    const decodeJwt = (t) => {
      try {
        const p = t.split(".")[1];
        return JSON.parse(atob(p.replace(/-/g, "+").replace(/_/g, "/")));
      } catch { return null; }
    };
    const token = localStorage.getItem("token") || "";
    const payload = decodeJwt(token) || {};
    const payerEmail = loginEmail || payload.email || "";

    // 4) Preferencia completa (usa itemsMP aquí)
    const preferenceBody = {
      subtotal,
      restauranteNombre: pedido.restauranteNombre,
      items: itemsMP, // 👈👈 AQUÍ sustituye el array anterior
      metadata: {
        ...metaDataValues,
        totalFinal,
        restauranteNombre: pedido.restauranteNombre,
        direccion: deliveryToUse.direccion,
        lat: deliveryToUse.lat,
        lng: deliveryToUse.lng,
        entregaTipo: deliveryOption,
      },
      paymentMethod: method,
      payer: { email: payerEmail }, // 👈 asegurado
      delivery: {
        direccion: deliveryToUse.direccion,
        lat: deliveryToUse.lat,
        lng: deliveryToUse.lng,
      },
      // opcional: ayuda a que tu lambda use esto en external_reference
      orderId: pedido.orderId,
    };

    // 5) Crear pedido en backend
    const res = await fetchDataCreateOrder(id, preferenceBody);

    if (!res) {
      showToast("error", "No se pudo crear el pedido. Inténtalo de nuevo.");
      return;
    }

    // 🔒 Cuenta desactivada por el admin
    if (res.statusCode === 403 && res.code === "USER_INACTIVE") {
      showToast("error", res.message || "Tu cuenta está desactivada.");
      return;
    }

    // 🟡 Restringido a Mercado Pago por un no-show anterior (intentó pagar en efectivo)
    if (res.statusCode === 403 && res.code === "USER_RESTRICTED_TO_MP") {
      showToast("error", res.message || "Por un no-show anterior, solo puedes pagar con Mercado Pago.");
      return;
    }

    // Otros errores (4xx/5xx)
    if (!(res.statusCode === 200 || res.statusCode === 201)) {
      showToast("error", res.message || "Error al crear pedido");
      return;
    }

    const wasCreated = res.statusCode === 201;                      // 👈 nuevo
    const alreadyExisted = res.statusCode === 200;                  // 👈 nuevo
    const existingMsg = (res.message || "").toLowerCase();
    const order = res.order || {};
    const newOrderId = order.orderId || pedido.orderId;

    if (alreadyExisted && existingMsg.includes("ya exist")) {
      // 👇 Toast informativo
      showToast("info", "La orden ya existía; no se modificó.");
    }

    if (method === "cash") {
      // Solo mostramos el modal si la orden ACABA de crearse
      if (wasCreated) {
        setCashOrderData({ orderId: newOrderId, metodo: "Efectivo" });
        setShowCashModal(true);
      } else {
        // Ya existía: no abras el modal de "gracias"
        // (opcional) Redirige o solo quédate en la página
        // navigate("/mis-pedidos"); // si quieres
      }
      return;
    }

    // 6) Mercado Pago
    // Si la orden ya existía, valida el estado de pago antes de crear preferencia
    if (order.paymentStatus === "pagado") {
      showToast("success", "Este pedido ya está pagado ✅");
      return;
    }
    if (order.paymentStatus === "cancelado") {
      showToast("warning", "Este pedido fue cancelado ❌");
      return;
    }

    // 💵 Si tenías una deuda pendiente por un no-show anterior, se suma a este pago.
    // Al confirmarse, se levanta la restricción y se limpia la deuda automáticamente.
    let itemsForMp = preferenceBody.items;
    if (res.pendingDebt > 0) {
      itemsForMp = [
        ...preferenceBody.items,
        {
          id: "deuda_no_show",
          title: "Deuda pendiente (no-show anterior)",
          quantity: 1,
          unit_price: Number(res.pendingDebt.toFixed(2)),
          currency_id: "MXN",
        },
      ];
      showToast(
        "info",
        `Se agregaron $${res.pendingDebt.toFixed(2)} de tu deuda pendiente a este pago. Al confirmarse, vuelves a poder pagar en efectivo.`
      );
    }

    // Si está pendiente (o no viene), permite crear la preferencia
    await createPreferencefetch({ ...preferenceBody, items: itemsForMp });
  };

  if (loading || loadingCreatePreference) return <Loader />;
  if (errorCreatePreference) return <div>Error al crear pago</div>;

  return (
    <section className="hacer-pedido-section container-mh container-fluid py-3 py-md-4">
      <div className="row justify-content-center g-3">
        <div className="col-12 col-lg-10 col-xl-8">
          <div className="hacer-pedido-page">
            <div className="row g-3 align-items-start">
              <div className="col-12">
                <h2>{loginName || "Cliente"} tu pedido es:</h2>
                {/* <h3>Pedido #{pedido.orderId}</h3>
                <p>Restaurante ID: {id}</p> */}
                <p>Restaurante Nombre: {pedido.restauranteNombre || "Desconocido"}</p>
              </div>

              <div className="col-12 col-lg-7">
                <ul className="hacer-pedido-items">
                  {pedido.items.map((item, idx) => (
                    <li key={idx}>
                      <span>{item.qty} x {item.name}</span>
                      <span>${(item.precio * item.qty).toFixed(2)}</span>
                    </li>
                  ))}
                  {bebidasAgregadas.map((b, idx) => (
                    <li key={`extra-${idx}`} style={{ color: "#059669" }}>
                      <span>1 x {b.name} <span style={{ fontSize: 11, opacity: 0.7 }}>(agregada)</span></span>
                      <span>${Number(b.precio).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="col-12 col-lg-5">
                <div className="hacer-pedido-summary">
                  <p>
                    Subtotal: <strong>${subtotal.toFixed(2)}</strong>
                  </p>
                  <p>
                    Envío: <strong>${envio.toFixed(2)}</strong>
                  </p>
                  {!responseCreatePreference?.id && (
                    <div className="propina-box">
                      <label>
                        Propina al conductor:
                        <select
                          value={propinaPct}
                          onChange={(e) => handleChangePropina(Number(e.target.value))}
                        >
                          <option value={0}>0%</option>
                          {[...Array(20)].map((_, i) => {
                            const val = (i + 1) * 5;
                            return (
                              <option key={val} value={val}>
                                {val}%
                              </option>
                            );
                          })}
                        </select>
                      </label>

                      <div className="propina-buttons">
                        <Button
                          text="No quiero dejar propina"
                          action={cancelarPropina}
                          color="red"
                          fill
                        />
                      </div>
                    </div>
                  )}

                  {propina > 0 && (
                    <p>
                      Propina: <strong>${propina.toFixed(2)}</strong>
                    </p>
                  )}

                  <p className="hacer-pedido-total">
                    Total: <strong>${(baseTotal + propina).toFixed(2)}</strong>
                  </p>
                </div>
              </div>

              {/* Entrega */}
              {!responseCreatePreference?.id && (
                <div className="col-12">
                  <div className="card shadow-sm mb-2">
                    <div className="card-body">
                      <h3 className="h5 mb-3">Entrega</h3>

                      {/* Opción CASA */}
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="entrega"
                          id="entrega-home"
                          value="home"
                          checked={deliveryOption === "home"}
                          disabled={casaFueraDeRango}
                          onChange={() => !casaFueraDeRango && setDeliveryOption("home")}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="entrega-home"
                          style={casaFueraDeRango ? { opacity: 0.5 } : {}}
                        >
                          <strong>Casa</strong>
                          <div className="small text-muted">
                            {userDelivery?.direccion
                              ? userDelivery.direccion
                              : "Aún no has guardado tu dirección"}
                          </div>
                          {casaFueraDeRango && distanciaCasa !== null && (
                            <div className="small text-danger mt-1">
                              🚫 Tu casa está a {distanciaCasa.toFixed(1)} km — fuera del radio de entrega ({RADIUS_KM_RESTAURANTS} km)
                            </div>
                          )}
                        </label>
                      </div>

                      {/* Opción UBICACIÓN ACTUAL / MAPA */}
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="entrega"
                          id="entrega-custom"
                          value="custom"
                          checked={deliveryOption === "custom"}
                          onChange={() => setDeliveryOption("custom")}
                        />
                        <label className="form-check-label" htmlFor="entrega-custom">
                          <strong>Otro lugar (usar mapa)</strong>
                          <div className="small text-muted">
                            {customDelivery?.direccion
                              ? `Seleccionado: ${customDelivery.direccion}`
                              : `Elige un punto dentro de ${RADIUS_KM_RESTAURANTS} km del restaurante`}
                          </div>
                          {customFueraDeRango && distanciaCustom !== null && (
                            <div className="small text-danger mt-1">
                              🚫 Este punto está a {distanciaCustom.toFixed(1)} km — fuera del radio de entrega ({RADIUS_KM_RESTAURANTS} km)
                            </div>
                          )}
                          {hasCustom && !customFueraDeRango && distanciaCustom !== null && (
                            <div className="small text-success mt-1">
                              ✅ {distanciaCustom.toFixed(1)} km del restaurante — dentro del rango
                            </div>
                          )}
                        </label>
                      </div>

                      {/* Botón para abrir mapa cuando está seleccionada la opción custom */}
                      {deliveryOption === "custom" && (
                        <div className="mt-2">
                          <Button
                            text={customDelivery ? "Cambiar ubicación" : "Elegir en el mapa"}
                            color="blue"
                            fill
                            action={() => setShowMap(true)}
                          />
                        </div>
                      )}

                      {/* Advertencia si no hay dirección guardada y eligió 'home' */}
                      {deliveryOption === "home" && !userDelivery?.direccion && (
                        <div className="alert alert-warning mt-3 mb-0">
                          Agrega tu dirección en tu perfil para poder recibir el pedido en "Casa",
                          o elige "Otro lugar" y marca el punto en el mapa.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {showMap && (
                <MapPickerLeaflet
                  initialLat={customDelivery?.lat ?? userDelivery?.lat ?? 19.432608}
                  initialLng={customDelivery?.lng ?? userDelivery?.lng ?? -99.133209}
                  geocodeEmail="tu-correo@dominio.com" // opcional para Nominatim
                  onPick={(lat, lng, direccion) => {
                    setCustomDelivery({
                      lat,
                      lng,
                      direccion: direccion || `Punto (${lat.toFixed(6)}, ${lng.toFixed(6)})`,
                    });
                    setShowMap(false);
                  }}
                  onClose={() => setShowMap(false)}
                />
              )}

              {/* 🔒 Se oculta una vez que ya se generó la preferencia de MP: si dejáramos
                  agregar bebidas después de eso, el total mostrado cambiaría pero la
                  preferencia (y el botón amarillo de MP) se quedarían con el total viejo. */}
              {bebidasSugeridas.length > 0 && !responseCreatePreference?.id && (
                <div className="col-12">
                  <div className="card shadow-sm mb-2">
                    <div className="card-body">
                      <p className="fw-bold mb-3">¿Le agregas algo de tomar? 🥤</p>
                      <div className="d-flex flex-wrap gap-3">
                        {bebidasSugeridas.map(b => {
                          const agregada = bebidasAgregadas.some(x => x.name === b.name);
                          return (
                            <label
                              key={b.name}
                              className="card"
                              style={{ minWidth: 140, flex: "1 1 140px", cursor: "pointer", border: agregada ? "2px solid #059669" : undefined }}
                            >
                              <div className="card-body p-2 d-flex flex-column gap-1">
                                <p className="fw-bold mb-0 small">{b.name}</p>
                                {b.descripcion && (
                                  <p className="text-muted mb-0" style={{ fontSize: 12 }}>{b.descripcion}</p>
                                )}
                                <p className="mb-2 small">${Number(b.precio).toFixed(2)}</p>
                                <div className="d-flex align-items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={agregada}
                                    onChange={() => toggleBebida(b)}
                                  />
                                  <span style={{ fontSize: 13 }}>{agregada ? "Agregada" : "Agregar"}</span>
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="col-12">
                <div className="metodo-pago">
                  <h3>Elige un método de pago</h3>
                  <div className="text-center">
                    {!responseCreatePreference?.id && (
                      <>
                        <Button
                          text="Pagar con Mercado Pago"
                          action={() => createPreferenceAction("mercadoPago")}
                          color="blue"
                          fill
                          classNames="mb-3 mt-1"
                          disabled={!isReadyDelivery}
                        />

                        <Button
                          text="Pagar en efectivo"
                          action={() => createPreferenceAction("cash")}
                          color="green"
                          fill
                          classNames="mb-3 mt-1 ms-lg-2"
                          disabled={!isReadyDelivery}
                        />
                      </>
                    )}
                  </div>

                  <div id="wallet_container" className="mt-2">
                    {responseCreatePreference?.id && (
                      // 👇 key: fuerza a React a desmontar y volver a montar el Wallet
                      // cada vez que cambia la preferencia (si no, el SDK de MP se queda
                      // con el checkout viejo y sigue mostrando el total anterior).
                      <Wallet
                        key={responseCreatePreference.id}
                        initialization={{ preferenceId: responseCreatePreference?.id }}
                        customization={{ texts: { valueProp: "smart_option" } }}
                        locale="es-MX"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        display={showCashModal}
        onClose={setShowCashModal}
        title="✅ Gracias por tu pedido"
        size="medium"
        fullMobile
      >
        <p><strong>Número de pedido:</strong> {cashOrderData?.orderId}</p>
        <p><strong>Método:</strong> {cashOrderData?.metodo}</p>
        <div className="text-center mt-3">
          <Button
            text="Aceptar"
            color="blue"
            fill
            action={() => {
              setShowCashModal(false);
              navigate("/");
            }}
          />
        </div>
      </Modal>
    </section>

  );
};

export default HacerPedido;